import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './WorkCarousel.module.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const WorkCarousel = ({ images = [] }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [slideStep, setSlideStep] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const pointerStartX = useRef(0);
  const prevFocusedRef = useRef(null);
  const suppressClickRef = useRef(false);
  const bodyStyleRef = useRef({});
  const htmlStyleRef = useRef({});
  const scrollYRef = useRef(0);

  const total = images.length;

  const safeSetIndex = useCallback((next) => {
    if (!total) return;
    setIndex(clamp(next, 0, total - 1));
  }, [total]);

  const goNext = useCallback(() => safeSetIndex(index + 1), [index, safeSetIndex]);
  const goPrev = useCallback(() => safeSetIndex(index - 1), [index, safeSetIndex]);

  const openLightbox = useCallback((nextIndex) => {
    prevFocusedRef.current = document.activeElement;
    setLightboxIndex(nextIndex);
    setZoomed(false);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setZoomed(false);
    if (prevFocusedRef.current && prevFocusedRef.current.focus) {
      prevFocusedRef.current.focus();
    }
  }, []);

  const onPointerDown = useCallback((event) => {
    if (!viewportRef.current) return;
    setIsDragging(true);
    suppressClickRef.current = false;
    viewportRef.current.setPointerCapture(event.pointerId);
    pointerStartX.current = event.clientX;
  }, []);

  const onPointerMove = useCallback((event) => {
    if (!isDragging) return;
    const deltaX = event.clientX - pointerStartX.current;
    if (Math.abs(deltaX) > 6) {
      suppressClickRef.current = true;
    }
    setDragOffset(deltaX);
  }, [isDragging]);

  const onPointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = slideStep * 0.18;
    if (dragOffset > threshold) {
      goPrev();
    } else if (dragOffset < -threshold) {
      goNext();
    }
    setDragOffset(0);
  }, [dragOffset, goNext, goPrev, isDragging, slideStep]);

  useEffect(() => {
    if (!viewportRef.current) return;
    const measure = () => {
      if (!trackRef.current) return;
      const slide = viewportRef.current.querySelector(`.${styles.slide}`);
      if (!slide) return;
      const slideWidth = slide.getBoundingClientRect().width;
      const gapValue = parseFloat(getComputedStyle(trackRef.current).columnGap || '0');
      setSlideStep(slideWidth + gapValue);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!total) return;
    const left = images[index - 1]?.src;
    const right = images[index + 1]?.src;
    [left, right].forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
  }, [images, index, total]);

  useEffect(() => {
    const onKey = (event) => {
      if (lightboxOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeLightbox();
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setLightboxIndex((prev) => clamp(prev + 1, 0, total - 1));
          return;
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setLightboxIndex((prev) => clamp(prev - 1, 0, total - 1));
          return;
        }
      } else {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goNext();
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goPrev();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeLightbox, goNext, goPrev, lightboxOpen, total]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const trap = (event) => {
      if (event.key !== 'Tab') return;
      const container = document.querySelector(`.${styles.lightboxDialog}`);
      if (!container) return;
      const focusable = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', trap);
    setTimeout(() => {
      const focusTarget = document.querySelector(`.${styles.lightboxClose}`);
      focusTarget?.focus();
    }, 0);

    return () => document.removeEventListener('keydown', trap);
  }, [lightboxOpen]);

  useEffect(() => {
    if (!lightboxOpen) return;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
    scrollYRef.current = scrollY;
    bodyStyleRef.current = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      paddingRight: body.style.paddingRight,
    };
    htmlStyleRef.current = {
      overflow: html.style.overflow,
      paddingRight: html.style.paddingRight,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    if (scrollbarGap > 0) {
      body.style.paddingRight = `${scrollbarGap}px`;
      html.style.paddingRight = `${scrollbarGap}px`;
    }

    return () => {
      const prev = bodyStyleRef.current;
      const htmlPrev = htmlStyleRef.current;
      html.style.overflow = htmlPrev.overflow || '';
      html.style.paddingRight = htmlPrev.paddingRight || '';
      body.style.overflow = prev.overflow || '';
      body.style.position = prev.position || '';
      body.style.top = prev.top || '';
      body.style.width = prev.width || '';
      body.style.paddingRight = prev.paddingRight || '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, [lightboxOpen]);

  const translateX = useMemo(() => {
    if (!slideStep) return 0;
    return -(index * slideStep) + dragOffset;
  }, [dragOffset, index, slideStep]);

  return (
    <section className={styles.section} aria-label="Portfolio gallery">
      <div className={styles.surface}>
        <div className={styles.viewport}>
          <button
            type="button"
            className={`${styles.navButton} ${styles.navPrev}`}
            onClick={goPrev}
            aria-label="Previous work"
            disabled={index === 0}
          >
            ‹
          </button>

          <div
            className={styles.viewportInner}
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div
              className={styles.track}
              ref={trackRef}
              style={{
                transform: `translate3d(${translateX}px, 0, 0)`,
                transition: isDragging ? 'none' : 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {images.map((image, idx) => (
                <button
                  key={image.id || image.src || idx}
                  type="button"
                  className={`${styles.slide} ${idx === index ? styles.isActive : ''}`}
                  onClick={() => {
                    if (suppressClickRef.current) {
                      suppressClickRef.current = false;
                      return;
                    }
                    openLightbox(idx);
                  }}
                  aria-label={`Open work ${idx + 1}`}
                >
                  <img
                    src={image.src}
                    alt={image.alt || `Work ${idx + 1}`}
                    loading={idx === index ? 'eager' : 'lazy'}
                    decoding="async"
                    className={styles.image}
                  />
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className={`${styles.navButton} ${styles.navNext}`}
            onClick={goNext}
            aria-label="Next work"
            disabled={index === total - 1}
          >
            ›
          </button>
        </div>

        <div className={styles.paginationRow}>
          <div className={styles.dots} role="tablist" aria-label="Gallery pagination">
            {images.map((_, idx) => (
              <button
                key={`dot-${idx}`}
                type="button"
                className={`${styles.dot} ${idx === index ? styles.dotActive : ''}`}
                onClick={() => safeSetIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
          <span className={styles.counter} aria-live="polite">
            {total ? `${index + 1} / ${total}` : '0 / 0'}
          </span>
        </div>
      </div>

      {lightboxOpen && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeLightbox();
          }}
        >
          <div className={styles.lightboxDialog}>
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={closeLightbox}
              aria-label="Close preview"
            >
              ✕
            </button>
            <div className={styles.lightboxMedia}>
              <img
                src={images[lightboxIndex]?.src}
                alt={images[lightboxIndex]?.alt || `Work ${lightboxIndex + 1}`}
                className={`${styles.lightboxImage} ${zoomed ? styles.lightboxZoom : ''}`}
                decoding="async"
                onClick={() => setZoomed((prev) => !prev)}
                draggable={false}
              />
            </div>
            <div className={styles.lightboxControls}>
              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                onClick={() => setLightboxIndex((prev) => clamp(prev - 1, 0, total - 1))}
                aria-label="Previous image"
                disabled={lightboxIndex === 0}
              >
                ‹
              </button>
              <span className={styles.lightboxCounter}>
                {total ? `${lightboxIndex + 1} / ${total}` : '0 / 0'}
              </span>
              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                onClick={() => setLightboxIndex((prev) => clamp(prev + 1, 0, total - 1))}
                aria-label="Next image"
                disabled={lightboxIndex === total - 1}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default WorkCarousel;
